import { useState, useEffect, useRef, useMemo } from "react"

interface UseDebouncedSelectionProps {
  initialValue: string[]
  onUpdate: (value: string[]) => void
  debounceMs?: number
  shouldUpdate?: boolean
}

const useDebouncedSelection = ({
  initialValue,
  onUpdate,
  debounceMs = 300,
  shouldUpdate = true,
}: UseDebouncedSelectionProps) => {
  const [selected, setSelected] = useState<string[]>(initialValue)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitializedWithRealData = useRef(false)

  // Update state with real initial data when it first becomes available
  useEffect(() => {
    // If we haven't initialized with real data yet, and shouldUpdate becomes true,
    // that means real data is now available - sync it once
    //
    // This might be outside the concern of the hook, but it makes it easier to
    // work with when passing in a collection that may still be loading
    if (!hasInitializedWithRealData.current && shouldUpdate) {
      setSelected(initialValue)
      hasInitializedWithRealData.current = true
    }
  }, [initialValue, shouldUpdate])

  const sortedSelected = useMemo(() => [...selected].sort(), [selected])
  const sortedInitialValue = useMemo(
    () => [...initialValue].sort(),
    [initialValue],
  )

  // Debounce and call onUpdate when selected changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    if (!shouldUpdate) return

    // Check if current selection differs from initial value
    const hasChanged =
      JSON.stringify(sortedSelected) !== JSON.stringify(sortedInitialValue)

    if (hasChanged) {
      debounceTimeoutRef.current = setTimeout(() => {
        onUpdate(selected)
      }, debounceMs)
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [
    selected,
    shouldUpdate,
    debounceMs,
    onUpdate,
    sortedSelected,
    sortedInitialValue,
  ])

  return {
    selected,
    setSelected,
  }
}

export default useDebouncedSelection
